import { useEffect, useReducer, useState } from "react";
import { useCentralApi, useCurrentUser } from "hooks/shared";
import { IWorkItemOverview } from "models/work-items";

export type UpdateWorkItemOverviewSignature = (workItem?: IWorkItemOverview, reportLoading?: boolean) => void;

type UseWorkItemOverviewResult = [
    IWorkItemOverview | null,
    boolean,
    UpdateWorkItemOverviewSignature,
    string | null
];

const useWorkItemOverview: (workItemId: number | null) => UseWorkItemOverviewResult = (workItemId) => {
    const [ result, setResult ] = useState<IWorkItemOverview | null>(null);
    const [ loading, setLoading ] = useState<boolean>(false);
    const [ error, setError ] = useState<string | null>(null);
    const [ reportLoading, setReportLoading ] = useState<boolean>(true);
    const [ shouldRefresh, refresh ] = useReducer(a => a + 1, 0);

    const user = useCurrentUser();
    const fetcher = useCentralApi(user);

    const updateOverview = (task?: IWorkItemOverview | null, reportLoading?: boolean) => {
        setReportLoading(reportLoading ?? false);
        if (task) {
            setResult(task);
        } else {
            refresh();
        }
    };
    
    useEffect(() => {
        setReportLoading(true);
    }, [ workItemId ]);

    useEffect(() => {
        if (!workItemId) return;
        const controller = new AbortController();

        (async () => {
            if (user && user.access_token) {
                setLoading(true);
                try {
                    const resp = await fetcher(`/api/v3/workitems/${workItemId}/overview`, {
                        signal: controller.signal
                    }, async (resp, err) => {
                        if (resp.status === 404) {
                            return true;
                        }
                        return false;
                    });

                    if (!resp.ok) {
                        // RESPOND TO ERRORS
                        switch (resp.status) {
                            case 404:
                                throw new Error('Work Item Not Found');
                            default:
                                break;
                        }
                    }

                    const data = await resp.json() as IWorkItemOverview;
                    setResult(data);
                } catch (err) {
                    // Will be done in BFLY-8658 or BFLY-9022                    
                } finally {
                    if (!controller.signal.aborted)
                        setLoading(false);
                }
            }
        })();

        return () => controller.abort();        
    }, [ workItemId, user, shouldRefresh ]);

    return [
        result,
        loading,
        updateOverview,
        error
    ];
};

export default useWorkItemOverview;